import { useState } from 'react';

import { useApiKeys, useCreateApiKey, useRevokeApiKey } from '@/hooks/useApiKeys';
import { Loading, ErrorBanner, EmptyState } from '@/components/common/States';
import { relTime } from '@/lib/format';
import type { ApiKeyCreated } from '@/types';

const ALL_SCOPES = ['read', 'run', 'manage'];

export function ApiKeys() {
  const { data, isLoading, error } = useApiKeys();
  const create = useCreateApiKey();
  const revoke = useRevokeApiKey();

  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>([]);
  const [created, setCreated] = useState<ApiKeyCreated | null>(null);
  const [formErr, setFormErr] = useState<string | null>(null);

  async function submit() {
    setFormErr(null);
    if (!name.trim()) return setFormErr('Give the key a name.');
    try {
      const key = await create.mutateAsync({ name: name.trim(), scopes });
      setCreated(key);
      setName('');
      setScopes([]);
    } catch (e) {
      setFormErr(e instanceof Error ? e.message : 'Failed to create key.');
    }
  }

  const active = (data ?? []).filter((k) => !k.revoked_at);

  return (
    <div className="fade-up">
      <div className="page-head">
        <div className="eyebrow">access</div>
        <div className="title">API keys</div>
        <div className="sub">
          Personal keys for the public REST API. Send as the <code>X-API-Key</code> header — they act as you.
        </div>
      </div>

      {created && (
        <div className="panel" style={{ borderColor: 'var(--good)', marginBottom: 16 }}>
          <div className="eyebrow" style={{ marginBottom: 8 }}>new key — copy it now</div>
          <div className="sub" style={{ marginBottom: 8 }}>
            This is the only time the full token is shown. Store it somewhere safe.
          </div>
          <code
            style={{
              display: 'block',
              padding: '10px 12px',
              background: 'rgba(84,240,168,0.08)',
              borderRadius: 8,
              wordBreak: 'break-all',
              userSelect: 'all',
            }}
          >
            {created.token}
          </code>
          <button
            className="btn"
            style={{ marginTop: 10 }}
            onClick={() => navigator.clipboard?.writeText(created.token)}
          >
            Copy
          </button>
          <button className="btn" style={{ marginTop: 10, marginLeft: 8 }} onClick={() => setCreated(null)}>
            Dismiss
          </button>
        </div>
      )}

      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="eyebrow" style={{ marginBottom: 10 }}>create a key</div>
        <div className="row" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 220px' }}>
            <label className="label">Name</label>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. CI bot, nightly runner"
            />
          </div>
          <div>
            <label className="label">Scopes (empty = full access)</label>
            <div style={{ display: 'flex', gap: 12, paddingTop: 6 }}>
              {ALL_SCOPES.map((s) => (
                <label key={s} style={{ display: 'flex', gap: 4, alignItems: 'center' }} className="muted">
                  <input
                    type="checkbox"
                    checked={scopes.includes(s)}
                    onChange={(e) =>
                      setScopes((prev) => (e.target.checked ? [...prev, s] : prev.filter((x) => x !== s)))
                    }
                  />
                  {s}
                </label>
              ))}
            </div>
          </div>
          <button className="btn btn-primary" onClick={submit} disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create key'}
          </button>
        </div>
        {formErr && <div className="sub" style={{ color: 'var(--bad)', marginTop: 8 }}>{formErr}</div>}
      </div>

      {isLoading ? (
        <Loading label="Loading keys" />
      ) : error ? (
        <ErrorBanner error={error} />
      ) : active.length === 0 ? (
        <EmptyState title="No API keys" hint="Create one above to call the API programmatically." />
      ) : (
        <div className="panel">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Prefix</th>
                <th>Scopes</th>
                <th>Created</th>
                <th>Last used</th>
                <th className="right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {active.map((k) => (
                <tr key={k.id}>
                  <td>{k.name}</td>
                  <td><code>{k.prefix}…</code></td>
                  <td className="muted">{k.scopes.length ? k.scopes.join(', ') : 'full'}</td>
                  <td className="muted">{relTime(k.created_at)}</td>
                  <td className="muted">{k.last_used_at ? relTime(k.last_used_at) : 'never'}</td>
                  <td className="right">
                    <button
                      className="btn"
                      onClick={() => {
                        if (confirm(`Revoke key "${k.name}"? Callers using it will get 401.`))
                          revoke.mutate(k.id);
                      }}
                    >
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
